import React, { createContext, useCallback, useState, useRef } from 'react';
import { AppAlertModal, AppConfirmModal, AppDangerConfirmModal } from '@/components/ui/AppDialog';

/**
 * Provider that powers `useConfirm()` / `useAlert()` / `useAppDialog()` hooks.
 * Mount once near the root of the app (already wired in App.js).
 */
export const AppDialogContext = createContext(null);


export function AppDialogProvider({ children }) {
  const [confirmState, setConfirmState] = useState(null);
  const [dangerState, setDangerState]   = useState(null);
  const [alertState, setAlertState]     = useState(null);

  const confirmResolveRef = useRef(null);
  const dangerResolveRef  = useRef(null);
  const alertResolveRef   = useRef(null);

  const confirm = useCallback((opts) => {
    if (confirmResolveRef.current) confirmResolveRef.current(false);
    return new Promise((resolve) => {
      confirmResolveRef.current = resolve;
      setConfirmState({ ...opts, _open: true });
    });
  }, []);

  const dangerConfirm = useCallback((opts) => {
    if (dangerResolveRef.current) dangerResolveRef.current(false);
    return new Promise((resolve) => {
      dangerResolveRef.current = resolve;
      setDangerState({ ...opts, _open: true });
    });
  }, []);

  const alert = useCallback((opts) => {
    if (alertResolveRef.current) alertResolveRef.current(true);
    return new Promise((resolve) => {
      alertResolveRef.current = resolve;
      const cfg = typeof opts === 'string' ? { description: opts } : (opts || {});
      setAlertState({ ...cfg, _open: true });
    });
  }, []);

  const closeConfirm = (val) => {
    if (confirmResolveRef.current) {
      confirmResolveRef.current(val);
      confirmResolveRef.current = null;
    }
    setConfirmState((s) => (s ? { ...s, _open: false } : null));
  };
  const closeDanger = (val) => {
    if (dangerResolveRef.current) {
      dangerResolveRef.current(val);
      dangerResolveRef.current = null;
    }
    setDangerState((s) => (s ? { ...s, _open: false } : null));
  };
  const closeAlert = () => {
    if (alertResolveRef.current) {
      alertResolveRef.current(true);
      alertResolveRef.current = null;
    }
    setAlertState((s) => (s ? { ...s, _open: false } : null));
  };

  return (
    <AppDialogContext.Provider value={{ confirm, dangerConfirm, alert }}>
      {children}

      {confirmState && (
        <AppConfirmModal
          open={!!confirmState._open}
          onOpenChange={(v) => { if (!v) closeConfirm(false); }}
          title={confirmState.title}
          description={confirmState.description}
          variant={confirmState.variant || 'info'}
          confirmLabel={confirmState.confirmLabel}
          cancelLabel={confirmState.cancelLabel}
          beforeAfter={confirmState.beforeAfter}
          infoNote={confirmState.infoNote}
          enterToConfirm={confirmState.enterToConfirm !== false}
          onConfirm={() => closeConfirm(true)}
          testId={confirmState.testId}
        >
          {confirmState.body}
        </AppConfirmModal>
      )}

      {dangerState && (
        <AppDangerConfirmModal
          open={!!dangerState._open}
          onOpenChange={(v) => { if (!v) closeDanger(false); }}
          title={dangerState.title}
          description={dangerState.description}
          confirmLabel={dangerState.confirmLabel}
          cancelLabel={dangerState.cancelLabel}
          requireTypeToConfirm={dangerState.requireTypeToConfirm}
          reasonRequired={dangerState.reasonRequired}
          onConfirm={(payload) => closeDanger(payload || true)}
          testId={dangerState.testId}
        >
          {dangerState.body}
        </AppDangerConfirmModal>
      )}

      {alertState && (
        <AppAlertModal
          open={!!alertState._open}
          onOpenChange={(v) => { if (!v) closeAlert(); }}
          title={alertState.title || 'Notice'}
          description={alertState.description}
          variant={alertState.variant || 'info'}
          okLabel={alertState.okLabel}
          onOk={() => closeAlert()}
          testId={alertState.testId}
        >
          {alertState.body}
        </AppAlertModal>
      )}
    </AppDialogContext.Provider>
  );
}
